/**
 * User-Safe Error Messages
 * Maps internal Firebase errors to generic user-friendly messages
 * Prevents information leakage about account existence, phone linking, etc.
 */

const ERROR_MESSAGES: Record<string, string> = {
  // Auth errors
  'auth/user-not-found': 'Invalid email or password',
  'auth/wrong-password': 'Invalid email or password',
  'auth/email-already-in-use': 'This email is already registered',
  'auth/weak-password': 'Password must be at least 6 characters',
  'auth/invalid-email': 'Please enter a valid email address',
  'auth/user-disabled': 'This account has been disabled',
  'auth/too-many-requests': 'Too many attempts. Please try again later',
  
  // Phone errors
  'auth/invalid-phone-number': 'Please enter a valid phone number',
  'auth/missing-phone-number': 'Phone number is required',
  'auth/quota-exceeded': 'SMS quota exceeded. Please try again later',
  'auth/invalid-verification-code': 'Invalid or expired verification code',
  'auth/credential-already-in-use': 'This phone number is already linked to another account',
  'auth/provider-already-linked': 'This authentication method is already linked',
  
  // Session errors
  'auth/id-token-expired': 'Your session has expired. Please sign in again',
  'auth/invalid-id-token': 'Your session is invalid. Please sign in again',
  'auth/session-cookie-expired': 'Your session has expired. Please sign in again',
  
  // Network errors
  'auth/network-request-failed': 'Network error. Please check your connection',
  'auth/internal-error': 'An error occurred. Please try again',
};

const GENERIC_ERROR = 'An error occurred. Please try again';

const INTERNAL_ERROR_PATTERNS = [
  /firebase/i,
  /auth\//i,
  /googleapis/i,
  /identitytoolkit/i,
];

/**
 * Sanitizes an error message before showing it to the user.
 * If the message appears to contain provider/internal details, returns fallback.
 */
export function sanitizeUserVisibleMessage(message: string | null | undefined, fallback: string = GENERIC_ERROR): string {
  if (!message || typeof message !== 'string') {
    return fallback;
  }

  const trimmed = message.trim();
  if (!trimmed) {
    return fallback;
  }

  const looksInternal = INTERNAL_ERROR_PATTERNS.some((pattern) => pattern.test(trimmed));
  return looksInternal ? fallback : trimmed;
}

/**
 * Converts a Firebase or internal error to a user-safe message
 * Logs the real error for debugging but returns generic message to user
 * @param error - The error object or message
 * @param context - Optional context for logging (e.g., 'signin', 'signup')
 * @returns User-safe error message
 */
export function getSafeErrorMessage(error: any, context?: string): string {
  // Log real error for debugging (will be in console/monitoring)
  if (context) {
    console.error(`[${context}] Error:`, error);
  } else {
    console.error('Error:', error);
  }

  // Extract error code if available
  const errorCode = error?.code || '';

  if (ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode];
  }

  // Never show raw provider/internal details for unknown auth errors.
  return sanitizeUserVisibleMessage(error?.message, GENERIC_ERROR);
}

/**
 * Checks if an error indicates the user should be shown a more specific message
 * Use this for non-sensitive errors where detailed feedback is helpful
 */
export function isUserActionableError(error: any): boolean {
  const code = error?.code || '';
  return [
    'auth/weak-password',
    'auth/invalid-email',
    'auth/invalid-phone-number',
    'auth/invalid-verification-code',
    'auth/too-many-requests',
  ].includes(code);
}
